import { useId, useMemo, useState, type ComponentType } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { ehAbaDaCasca, PERMISSAO_CONFIGURAR_SUBSISTEMAS, paginaNoSubsistema, paginasDaConfig, paginasSemLugar, PREFIXO_ABA_CASCA, type ConfigSubsistemas } from '@malgoos/shared';
import { Abas } from '../../componentes/abas';
import { Botao, Cartao, Selo } from '../../componentes/base';
import { Carregando, Erro } from '../../componentes/estados';
import { Alternador, Selecao } from '../../componentes/formulario';
import { Modal } from '../../componentes/sobreposicao';
import { useToast } from '../../componentes/toast';
import { api, ErroRequisicao, get, mensagemDeErro } from '../../nucleo/api';
import { useSessao, veTodasAsEmpresas } from '../../nucleo/sessao';
import { Historico } from './Historico';
import {
  alternarModulo,
  alternarPagina,
  alternarSubsistema,
  doEscopo,
  efetivaDoEscopo,
  estadoDoModulo,
  restaurarSubsistema,
  restaurarTudo,
  subsistemasDoEscopo,
  type Escopo,
} from './subsistemas-config';

/** Resposta de `GET /configuracoes/subsistemas`. */
export interface DadosSubsistemasConfig {
  workspace: ConfigSubsistemas;
  empresas: { id: string; nome: string; config: ConfigSubsistemas | null }[];
  paginas: { slug: string; aba: string }[];
}

/** Encaixe das seções do workspace que vivem na mesma tela (D2 ícones, D3 links): recebem e devolvem a configuração. */
export interface PropsSecaoWorkspace {
  config: ConfigSubsistemas;
  aoMudar: (c: ConfigSubsistemas) => void;
  /** Grava a arrumação do workspace inteira (o mesmo PUT do botão Salvar de cima). */
  aoSalvar: () => void;
  salvando: boolean;
}

const WORKSPACE = 'workspace';

/** Review r43 segurança (B1) = spec (M1): o "Valer para" inicial; quem não vê todas parte da empresa atual (ou da primeira dele). */
export function valerInicialSubsistemas(veTodas: boolean, atual: string | null, empresas: readonly { id: string }[]): string {
  if (veTodas || !empresas.length) return WORKSPACE;
  return empresas.find((e) => e.id === atual)?.id ?? empresas[0]!.id;
}

/**
 * D1 (sub-sistemas, L3): Admin > Configurações > Sub-sistemas. Liga os sub-sistemas no workspace, escolhe o escopo
 * ("valer para": o workspace ou uma empresa, que sobrepõe o workspace, decisão D2) e, por sub-sistema, liga e desliga o
 * módulo inteiro (linha do módulo com "parcial") e cada página. "Restaurar padrão" por sub-sistema e geral. Ao salvar, a
 * página que fica sem lugar abre a confirmação com consentimento (o servidor exige o mesmo). Layout com os componentes de
 * sempre (Cartao, Alternador, Abas, Selecao, Modal).
 */
export function Subsistemas({ secoesWorkspace }: { secoesWorkspace?: ComponentType<PropsSecaoWorkspace>[] }) {
  const { t } = useTranslation('configuracoes');
  const s = useSessao();
  const q = useQuery({ queryKey: ['configuracoes', 'subsistemas'], queryFn: () => get<DadosSubsistemasConfig>('/configuracoes/subsistemas'), enabled: s.pode(PERMISSAO_CONFIGURAR_SUBSISTEMAS) });
  if (!s.pode(PERMISSAO_CONFIGURAR_SUBSISTEMAS)) {
    return (
      <Cartao titulo={t('subsistemas.titulo')}>
        <p className="text-sm text-tinta-3">{t('subsistemas.semPermissao')}</p>
      </Cartao>
    );
  }
  if (q.isPending) return <Carregando />;
  if (q.isError) return <Erro erro={q.error} aoTentar={() => q.refetch()} />;
  return <Editor key={JSON.stringify(q.data)} dados={q.data} secoesWorkspace={secoesWorkspace ?? []} />;
}

function Editor({ dados, secoesWorkspace }: { dados: DadosSubsistemasConfig; secoesWorkspace: ComponentType<PropsSecaoWorkspace>[] }) {
  const { t } = useTranslation('configuracoes');
  const { t: tg } = useTranslation();
  const s = useSessao();
  const qc = useQueryClient();
  const toast = useToast();
  const idUsar = useId();
  const idNovo = useId();
  const idComunicacao = useId();
  const idValer = useId();
  const [workspace, setWorkspace] = useState<ConfigSubsistemas>(dados.workspace);
  const [empresas, setEmpresas] = useState<Record<string, ConfigSubsistemas | null>>(() => Object.fromEntries(dados.empresas.map((e) => [e.id, e.config])));
  // Review r43 segurança (B1): a arrumação do workspace exige ver todas as empresas (o servidor recusa com
  // `permissao.padrao_workspace_exige_todas`); quem não vê todas arruma só a das empresas dele, a partir da atual.
  const veTodas = veTodasAsEmpresas(s);
  const [valer, setValer] = useState(valerInicialSubsistemas(veTodas, s.empresas?.atual ?? null, dados.empresas));
  const [sub, setSub] = useState(subsistemasDoEscopo({ empresaId: null, workspace: dados.workspace, empresa: null })[0]!.sub);
  const [abertos, setAbertos] = useState<Set<string>>(new Set());
  const [semLugar, setSemLugar] = useState<string[] | null>(null);
  const [ciente, setCiente] = useState(false);

  const escopo: Escopo = { empresaId: valer === WORKSPACE ? null : valer, workspace, empresa: valer === WORKSPACE ? null : (empresas[valer] ?? null) };
  const efetiva = efetivaDoEscopo(escopo);
  const mudar = (e: Escopo) => {
    if (e.empresaId) setEmpresas((x) => ({ ...x, [e.empresaId!]: e.empresa }));
    else setWorkspace(e.workspace);
  };
  // Val8: as páginas da casca nova (`casca-<chave>`) só com o novo desenho (na casca de sempre não aparecem no menu).
  const comNovoDesenho = !!efetiva.ativo && !!efetiva.novoDesenho;
  const porModulo = useMemo(() => {
    const m = new Map<string, string[]>();
    for (const p of paginasDaConfig({ ativo: comNovoDesenho, novoDesenho: comNovoDesenho }, dados.paginas)) m.set(p.slug, [...(m.get(p.slug) ?? []), p.aba]);
    return [...m.entries()];
  }, [dados.paginas, comNovoDesenho]);
  const nomeSub = (x: string) => tg(`subsistemas.nomes.${x}`);
  const rotuloDaPagina = (slug: string, aba: string) => (ehAbaDaCasca(aba) ? tg(`${slug}:casca.${aba.slice(PREFIXO_ABA_CASCA.length)}`) : tg(`${slug}:abas.${aba}`));
  const nomePagina = (chave: string) => {
    const [slug, aba] = chave.split('.') as [string, string];
    return `${tg(`${slug}:nome`)} > ${rotuloDaPagina(slug, aba)}`;
  };

  const salvar = useMutation({
    mutationFn: (comCiencia: boolean) => api<{ alterado: boolean }>('PUT', '/configuracoes/subsistemas', { empresa_id: escopo.empresaId, config: doEscopo(escopo), ...(comCiencia ? { ciente_sem_lugar: true } : {}) }),
    onSuccess: async (r) => {
      setSemLugar(null);
      setCiente(false);
      await s.recarregar();
      await qc.invalidateQueries({ queryKey: ['configuracoes', 'subsistemas'] });
      await qc.invalidateQueries({ queryKey: ['configuracoes', 'historico'] });
      toast.sucesso(r.alterado ? t('subsistemas.salvo') : t('subsistemas.semMudanca'));
    },
    onError: (e) => {
      if (e instanceof ErroRequisicao && e.corpo.code === 'subsistemas.confirmar_sem_lugar') {
        const meta = e.metaDoErro();
        setSemLugar(Array.isArray(meta?.paginas) ? (meta.paginas as string[]) : []);
        return;
      }
      toast.erro(mensagemDeErro(e));
    },
  });
  // Aviso antes de salvar (a mesma regra do servidor): quantas páginas ficam sem lugar com o que está na tela.
  const semLugarAgora = paginasSemLugar(efetiva, dados.paginas).length;

  return (
    <div className="grid min-w-0 gap-4 xl:grid-cols-3">
      <div className="flex min-w-0 flex-col gap-4 xl:col-span-2">
        <p className="text-sm text-tinta-3">{t('subsistemas.ajuda')}</p>
        <Cartao titulo={t('subsistemas.titulo')}>
          <div className="flex min-w-0 flex-col gap-4">
            <div className="flex min-w-0 items-start justify-between gap-3">
              <div className="min-w-0">
                <label htmlFor={idUsar} className="text-sm font-medium text-tinta-1">
                  {t('subsistemas.usar')}
                </label>
                <p className="text-sm text-tinta-3">{t('subsistemas.usarAjuda')}</p>
                {/* Review r44 spec (B1) = visual (B1): o restrito vê o alternador desabilitado com o motivo. */}
                {!veTodas && (
                  <p data-subsistemas-so-todas="" className="text-xs text-tinta-3">
                    {t('subsistemas.usarSoTodas')}
                  </p>
                )}
              </div>
              <Alternador id={idUsar} marcado={!!workspace.ativo} desabilitado={!veTodas} aoMudar={(v) => setWorkspace((w) => ({ ...w, ativo: v }))} />
            </div>
            {/* CRM novo desenho (N7): a casca nova do workspace, só com os sub-sistemas ligados. */}
            <div className="flex min-w-0 items-start justify-between gap-3" data-novo-desenho="">
              <div className="min-w-0">
                <label htmlFor={idNovo} className="text-sm font-medium text-tinta-1">
                  {t('subsistemas.novoDesenho')}
                </label>
                <p className="text-sm text-tinta-3">{t('subsistemas.novoDesenhoAjuda')}</p>
              </div>
              <Alternador id={idNovo} marcado={!!workspace.novoDesenho} desabilitado={!veTodas || !workspace.ativo} aoMudar={(v) => setWorkspace((w) => ({ ...w, novoDesenho: v }))} />
            </div>
            {/* Comunicação unificada (04/11): liga por workspace, para mostrar no Sandbox antes do Principal. */}
            <div className="flex min-w-0 items-start justify-between gap-3" data-comunicacao-unificada="">
              <div className="min-w-0">
                <label htmlFor={idComunicacao} className="text-sm font-medium text-tinta-1">
                  {t('subsistemas.comunicacaoUnificada')}
                </label>
                <p className="text-sm text-tinta-3">{t('subsistemas.comunicacaoUnificadaAjuda')}</p>
              </div>
              <Alternador id={idComunicacao} marcado={!!workspace.comunicacaoUnificada} desabilitado={!veTodas} aoMudar={(v) => setWorkspace((w) => ({ ...w, comunicacaoUnificada: v }))} />
            </div>
            <div className="flex min-w-0 flex-col gap-1">
              <label htmlFor={idValer} className="text-sm font-medium text-tinta-1">
                {t('subsistemas.valerPara')}
              </label>
              <Selecao
                id={idValer}
                value={valer}
                onChange={(e) => setValer(e.target.value)}
                opcoes={[...(veTodas ? [{ valor: WORKSPACE, rotulo: t('subsistemas.oWorkspace') }] : []), ...dados.empresas.map((e) => ({ valor: e.id, rotulo: t('subsistemas.aEmpresa', { nome: e.nome }) }))]}
              />
              {escopo.empresaId && <p className="text-xs text-tinta-3">{t('subsistemas.empresaAjuda')}</p>}
            </div>
            <Abas grupo="subsistemas-config" variante="chips" rotulo={t('subsistemas.subsistema')} ativa={sub} aoMudar={setSub} itens={subsistemasDoEscopo(escopo).map((x) => ({ chave: x.sub, rotulo: nomeSub(x.sub) }))} />
            <div className="flex min-w-0 flex-col gap-3" data-subsistema-config={sub}>
              <div className="flex min-w-0 flex-wrap items-center justify-between gap-3 rounded-lg border border-borda p-3">
                <div className="min-w-0">
                  <span className="text-sm font-semibold text-tinta-1">{t('subsistemas.ligadoEm', { nome: nomeSub(sub) })}</span>
                  <p className="text-xs text-tinta-3">{t('subsistemas.ligadoAjuda')}</p>
                </div>
                <Alternador
                  rotulo={t('subsistemas.ligadoEm', { nome: nomeSub(sub) })}
                  marcado={!(efetiva.desligados ?? []).includes(sub)}
                  aoMudar={(v) => mudar(alternarSubsistema(escopo, sub, v))}
                />
              </div>
              <p className="text-xs text-tinta-3">{t('subsistemas.comum')}</p>
              <ul className="flex min-w-0 flex-col divide-y divide-borda rounded-lg border border-borda">
                {porModulo.map(([slug, abas]) => {
                  const { estado, ligadas } = estadoDoModulo(efetiva, sub, slug, abas);
                  const aberto = abertos.has(slug);
                  const idLista = `paginas-${sub}-${slug}`;
                  const nomeModulo = tg(`${slug}:nome`);
                  return (
                    <li key={slug} className="min-w-0" data-modulo-config={slug}>
                      <div className="flex min-w-0 items-center gap-3 px-3 py-2.5">
                        <button
                          type="button"
                          aria-expanded={aberto}
                          aria-controls={idLista}
                          onClick={() => setAbertos((x) => (x.has(slug) ? new Set([...x].filter((y) => y !== slug)) : new Set([...x, slug])))}
                          className="flex min-w-0 flex-1 items-center gap-2 rounded-md text-left text-sm font-medium text-tinta-1 hover:text-acento-forte"
                        >
                          {aberto ? <ChevronDown className="size-4 shrink-0 text-tinta-3" aria-hidden /> : <ChevronRight className="size-4 shrink-0 text-tinta-3" aria-hidden />}
                          <span className="min-w-0 truncate" title={nomeModulo}>{nomeModulo}</span>
                          <span className="shrink-0 text-xs font-normal text-tinta-3">{t('subsistemas.paginasDe', { ligadas, total: abas.length })}</span>
                          {estado === 'parcial' && <Selo cor="laranja">{t('subsistemas.parcial')}</Selo>}
                        </button>
                        <Alternador
                          rotulo={t('subsistemas.modulo', { nome: nomeModulo, subsistema: nomeSub(sub) })}
                          marcado={estado === 'ligado' || estado === 'parcial'}
                          aoMudar={(v) => mudar(v && estado === 'nenhuma' ? abas.reduce((e, aba) => alternarPagina(e, sub, slug, aba, true), alternarModulo(escopo, sub, slug, true)) : alternarModulo(escopo, sub, slug, v))}
                        />
                      </div>
                      {aberto && (
                        <ul id={idLista} className="flex min-w-0 flex-col gap-1 border-t border-borda bg-sup-2 px-3 py-2">
                          {abas.map((aba) => {
                            const rotulo = rotuloDaPagina(slug, aba);
                            const casca = ehAbaDaCasca(aba);
                            return (
                              <li key={aba} className="flex min-w-0 items-center justify-between gap-3 py-1 pl-6 text-sm text-tinta-2" data-pagina-config={`${slug}.${aba}`}>
                                <span className="flex min-w-0 items-center gap-2">
                                  <span className="min-w-0 truncate" title={rotulo}>{rotulo}</span>
                                  {casca && <Selo>{t('subsistemas.paginaCasca')}</Selo>}
                                </span>
                                <Alternador
                                  rotulo={t('subsistemas.pagina', { pagina: rotulo, subsistema: nomeSub(sub) })}
                                  marcado={paginaNoSubsistema(efetiva, sub, slug, aba)}
                                  desabilitado={estado === 'desligado'}
                                  aoMudar={(v) => mudar(alternarPagina(escopo, sub, slug, aba, v))}
                                />
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
            {semLugarAgora > 0 && <p className="text-sm text-laranja">{t('subsistemas.avisoSemLugar', { count: semLugarAgora })}</p>}
            <div className="flex flex-wrap items-center justify-end gap-2">
              <Botao variante="fantasma" onClick={() => mudar(restaurarSubsistema(escopo, sub))}>
                {t('subsistemas.restaurarSub', { nome: nomeSub(sub) })}
              </Botao>
              <Botao variante="fantasma" onClick={() => mudar(restaurarTudo(escopo))}>
                {t('subsistemas.restaurarTudo')}
              </Botao>
              <Botao variante="primario" carregando={salvar.isPending} onClick={() => salvar.mutate(false)}>
                {t('subsistemas.salvar')}
              </Botao>
            </div>
          </div>
        </Cartao>
        {/* D2 e D3: seções do workspace na mesma tela (ícones nas abas, links externos), salvas pelo mesmo botão. */}
        {!escopo.empresaId && secoesWorkspace.map((Secao, i) => <Secao key={i} config={workspace} aoMudar={setWorkspace} aoSalvar={() => salvar.mutate(false)} salvando={salvar.isPending} />)}
      </div>
      {s.pode('configuracoes.ver') && (
        <Historico
          entidade="configuracoes.subsistemas"
          titulo={t('subsistemas.historico')}
          linhas={(i) => {
            const d = (i.depois ?? {}) as { empresa_id?: string | null };
            const nome = d.empresa_id ? dados.empresas.find((e) => e.id === d.empresa_id)?.nome : null;
            return [nome ? t('subsistemas.trocaEmpresa', { nome }) : t('subsistemas.trocaWorkspace')];
          }}
        />
      )}
      <Modal
        aberto={semLugar !== null}
        aoFechar={() => {
          setSemLugar(null);
          setCiente(false);
        }}
        titulo={t('subsistemas.semLugarTitulo')}
        rodape={
          <>
            <Botao
              variante="fantasma"
              onClick={() => {
                setSemLugar(null);
                setCiente(false);
              }}
            >
              {tg('comum.cancelar')}
            </Botao>
            <Botao variante="primario" disabled={!ciente} carregando={salvar.isPending} onClick={() => salvar.mutate(true)}>
              {t('subsistemas.confirmar')}
            </Botao>
          </>
        }
      >
        <div className="flex flex-col gap-3 text-sm text-tinta-2">
          <p>{t('subsistemas.semLugarTexto')}</p>
          <ul className="list-disc pl-5" data-sem-lugar>
            {(semLugar ?? []).map((p) => (
              <li key={p}>{nomePagina(p)}</li>
            ))}
          </ul>
          <label className="flex cursor-pointer items-start gap-2 text-tinta-1">
            <input type="checkbox" className="mt-0.5" checked={ciente} onChange={(e) => setCiente(e.target.checked)} data-ciente />
            <span>{t('subsistemas.ciente')}</span>
          </label>
        </div>
      </Modal>
    </div>
  );
}
